import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';

import { OverheadCategory } from '../../models/OverheadCategory';
import { ObjectWithFinancialData } from '../../models/Financial/ObjectWithFinancialData';
import { OverheadCategoryDetailModalComponent } from '../../yuki/overhead-category-detail-modal/overhead-category-detail-modal.component';
import { GetOverheadCategoriesFinancialOverviewData } from '../financial.actions';
import { FinancialState } from '../financial.state';
import { YukiState } from '../../yuki/yuki.state';
import { OverheadCategoryBudgetModalComponent } from '../overhead-category-budget-modal/overhead-category-budget-modal.component';
import { OverheadCategoryBudgetDetailsModalComponent } from '../overhead-category-budget-details-modal/overhead-category-budget-details-modal.component';
import { GetAllOverheadCategories, SetOverheadCategoryHidden } from '../../yuki/yuki.actions';

@Component({
    selector: 'app-overhead-financial-graphs',
    templateUrl: './overhead-financial-graphs.component.html',
    styleUrls: ['./overhead-financial-graphs.component.scss'],
})
export class OverheadFinancialGraphsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() selectedYear = new Date().getFullYear();
    @Select(YukiState.overheadCategories) allOverheadCategories$: Observable<OverheadCategory[]>;
    
    overheadCategoriesWithFinancialData: ObjectWithFinancialData[];
    datasource: MatTableDataSource<ObjectWithFinancialData>;
    displayedColumns: string[] = ['overheadCategory', 'total', 'average', 'budget', 'alert', 'edit'];
    showHidden = false;

    fetchedDataForCurrentYear = false;
    loadingData = false;
    private allOverheadCategories: OverheadCategory[] = [];
    private sSort: MatSort;

    private subscriptions = new Subscription();

    constructor(private store: Store, private modalService: NgbModal) {}

    @ViewChild('sort') set sort(ms: MatSort) {
        this.sSort = ms;
        this.setDataSourceAttributes();
    }

    setDataSourceAttributes(): void {
        if (this.datasource) {
            this.datasource.sort = this.sSort;
        }
    }

    ngOnInit(): void {
        // Subscribe to all overhead categories for sorting by name
        this.subscriptions.add(
            this.allOverheadCategories$.subscribe((categories) => {
                this.allOverheadCategories = categories || [];
            })
        );

        this.createMatTable();
        this.fetchData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedYear && !changes.selectedYear.firstChange) {
            this.fetchedDataForCurrentYear = false;
            this.fetchData();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openOverheadCategoryModal(overheadCategoryWithFinancialOverviewData: ObjectWithFinancialData): void {
        const modalRef = this.modalService.open(OverheadCategoryDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.overheadCategoryId = overheadCategoryWithFinancialOverviewData.objectId;
        modalRef.componentInstance.openFinancialTab = true;
    }

    openEditBudgetModal(overheadCategoryWithFinancialOverviewData: ObjectWithFinancialData): void {
        const modalRef = this.modalService.open(OverheadCategoryBudgetModalComponent, {
            windowClass: 'modal-prompt',
            animation: false,
        });
        const category = this.allOverheadCategories.find((item) => item.id === overheadCategoryWithFinancialOverviewData.objectId);
        modalRef.componentInstance.categoryId = overheadCategoryWithFinancialOverviewData.objectId;
        modalRef.componentInstance.categoryName = category?.categoryName || 'Overhead category';
        modalRef.componentInstance.year = this.selectedYear;

        this.subscriptions.add(
            modalRef.closed.subscribe((updated: boolean) => {
                if (updated) {
                    this.reloadFinancialOverviewData();
                }
            }),
        );
    }

    openViewDetailsModal(overheadCategoryWithFinancialOverviewData: ObjectWithFinancialData): void {
        const modalRef = this.modalService.open(OverheadCategoryBudgetDetailsModalComponent, {
            windowClass: 'modal-prompt modal-prompt--wide',
            size: 'xl',
            animation: false,
        });
        const category = this.allOverheadCategories.find((item) => item.id === overheadCategoryWithFinancialOverviewData.objectId);
        modalRef.componentInstance.categoryId = overheadCategoryWithFinancialOverviewData.objectId;
        modalRef.componentInstance.categoryName = category?.categoryName || 'Overhead category';
        modalRef.componentInstance.year = this.selectedYear;
    }

    private fetchData(): void {
        const year: number = this.selectedYear;

        this.subscriptions.add(
            this.store.select(FinancialState.overheadCategoriesFinancialOverviewData(year)).subscribe((overheadCategoriesWithFinancialOverviewData) => {
                if (
                    !this.fetchedDataForCurrentYear &&
                    (!overheadCategoriesWithFinancialOverviewData || overheadCategoriesWithFinancialOverviewData.length < 1)
                ) {
                    this.store.dispatch(new GetOverheadCategoriesFinancialOverviewData(year, this.showHidden));
                    this.fetchedDataForCurrentYear = true;
                    this.loadingData = true;
                }

                this.loadingData = false;
                this.overheadCategoriesWithFinancialData = overheadCategoriesWithFinancialOverviewData;
                this.datasource.data = overheadCategoriesWithFinancialOverviewData;
                this.datasource.sort = this.sSort;
                
                // Custom sorting data accessor for proper column sorting
                this.datasource.sortingDataAccessor = (item: ObjectWithFinancialData, property: string) => {
                    switch (property) {
                        case 'overheadCategory':
                            // Sort by overhead category name instead of UUID
                            const category = this.allOverheadCategories.find(c => c.id === item.objectId);
                            return category?.categoryName?.toLowerCase() || '';
                        case 'total':
                            return item.total || 0;
                        case 'average':
                            return item.average || 0;
                        case 'budget':
                            return item.budget || 0;
                        case 'alert':
                            return this.getAlertPriority(item);
                        default:
                            return (item as any)[property];
                    }
                };
            }),
        );
    }

    onShowHiddenChange(): void {
        this.reloadFinancialOverviewData();
    }

    isCategoryHidden(financialData: ObjectWithFinancialData): boolean {
        const category = this.allOverheadCategories.find((item) => item.id === financialData.objectId);
        return !!category?.hidden;
    }

    toggleCategoryHidden(financialData: ObjectWithFinancialData): void {
        const category = this.allOverheadCategories.find((item) => item.id === financialData.objectId);
        if (!category?.id) {
            return;
        }

        this.loadingData = true;
        this.subscriptions.add(
            this.store.dispatch(new SetOverheadCategoryHidden(category.id, !category.hidden)).subscribe(() => {
                this.store.dispatch(new GetAllOverheadCategories());
                this.reloadFinancialOverviewData();
            }),
        );
    }

    getAlert(financialData: ObjectWithFinancialData): { level: 'ok' | 'over-budget' | 'over-monthly-pace'; label: string; details?: string } {
        const totalCosts = Number(financialData?.total || 0);
        const yearlyBudget = Number(financialData?.budget || 0);
        const monthsInScope = this.getMonthsInScope();
        const monthlyBudget = yearlyBudget / 12;
        const averageCostsPerMonth = monthsInScope > 0 ? totalCosts / monthsInScope : totalCosts;

        if (totalCosts > yearlyBudget) {
            return {
                level: 'over-budget',
                label: '!',
                details: 'Over annual budget',
            };
        }

        if (averageCostsPerMonth > monthlyBudget) {
            return {
                level: 'over-monthly-pace',
                label: '!',
                details: 'Over monthly budget pace',
            };
        }

        return {
            level: 'ok',
            label: '✓',
            details: 'OK',
        };
    }

    /**
     * For 'over-monthly-pace': returns the overage vs expected-to-date spend,
     * expressed as { overageEur, overagePct }.
     * Expected-to-date = (yearlyBudget / 12) * monthsInScope.
     */
    getOverageDetails(financialData: ObjectWithFinancialData): { overageEur: number; overagePct: number } | null {
        const alert = this.getAlert(financialData);
        if (alert.level !== 'over-monthly-pace' && alert.level !== 'over-budget') return null;

        const totalCosts = Number(financialData?.total || 0);
        const yearlyBudget = Number(financialData?.budget || 0);
        if (!yearlyBudget) return null;

        const monthsInScope = this.getMonthsInScope();
        const expectedToDate = (yearlyBudget / 12) * monthsInScope;
        if (!expectedToDate) return null;

        const overageEur = totalCosts - expectedToDate;
        const overagePct = (overageEur / expectedToDate) * 100;
        return { overageEur, overagePct };
    }

    getOverageTooltip(overageEur: number): string {
        return `Overschrijding t.o.v. verwacht budget tot vandaag: €${overageEur.toFixed(2)}`;
    }

    private getAlertPriority(financialData: ObjectWithFinancialData): number {
        const level = this.getAlert(financialData).level;
        if (level === 'over-budget') {
            return 2;
        }
        if (level === 'over-monthly-pace') {
            return 1;
        }
        return 0;
    }

    private getMonthsInScope(): number {
        const now = new Date();
        if (this.selectedYear < now.getFullYear()) {
            return 12;
        }
        if (this.selectedYear > now.getFullYear()) {
            return 1;
        }

        return now.getMonth() + 1;
    }

    private createMatTable(): void {
        this.datasource = new MatTableDataSource<ObjectWithFinancialData>();
    }

    private reloadFinancialOverviewData(): void {
        this.loadingData = true;
        this.fetchedDataForCurrentYear = true;
        this.store.dispatch(new GetOverheadCategoriesFinancialOverviewData(this.selectedYear, this.showHidden));
    }
}
